import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { JobList } from '../components/jobs/JobList';
import { JobFilters } from '../components/jobs/JobFilters';
import { Job } from '../types/jobs';
import { Briefcase, MailCheck, Award, PlusCircle, ExternalLink } from 'lucide-react';
import { Pagination } from '../components/ui/Pagination';
import { SEO } from '../components/SEO';
import * as amplitude from '@amplitude/analytics-browser';
import { openWebRedirect } from '../lib/api';
import { fetchJobCategories, fetchJobsOffers, fetchFeaturedJobs, fetchJobById } from '../lib/api';
import { JobDetailModal } from '../components/jobs/JobDetailModal';

const JOBS_PER_PAGE = 9;

export function JobOffersPage() {
  const { user, userInfo } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [regularJobs, setRegularJobs] = React.useState<Job[]>([]);
  const [premiumJobs, setPremiumJobs] = React.useState<Job[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalJobs, setTotalJobs] = React.useState(0);
  const hasTrackedRef = React.useRef(false);
  const [jobCategories, setJobCategories] = React.useState([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = React.useState(true);
  const [categoryMap, setCategoryMap] = React.useState<Record<string, string>>({});
  const [categoryTotals, setCategoryTotals] = React.useState<Record<string, number>>({});
  // State for job detail modal
  const [selectedJob, setSelectedJob] = React.useState<Job | null>(null);
  const [isJobModalOpen, setIsJobModalOpen] = React.useState(false);
  const [isJobDetailLoading, setIsJobDetailLoading] = React.useState(false);
  const [jobDetailError, setJobDetailError] = React.useState<string | null>(null);

  // Get page from URL or default to 1
  const currentPage = Number(searchParams.get('page') || 1);
  
  // Calculate the offset based on current page and jobs per page
  const offset = (currentPage - 1) * JOBS_PER_PAGE;
  
  // Get category filter from URL
  const categoryParam = searchParams.get('category');
  const selectedCategory = categoryParam ? parseInt(categoryParam) : null;

  // Redirect to home if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  React.useEffect(() => {
    if (!hasTrackedRef.current) {
      // Analytics.
      amplitude.track("Jobs Displayed");
      hasTrackedRef.current = true;
    }
  }, []);

  // Fetch job categories from API
  React.useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsCategoriesLoading(true);
        // Check if userInfo exists before making the API call
        if (!userInfo) {
          console.error('User info not available for fetching categories');
          return;
        }
        const categories = await fetchJobCategories(userInfo);
        setJobCategories(categories);

        const map: Record<string, string> = {};
        const totals: Record<string, number> = {};
        categories.forEach((category: any) => {
          map[String(category.code)] = category.name;
          totals[String(category.code)] = category.count;
        });
        setCategoryMap(map);
        setCategoryTotals(totals);
      } catch (error) {
        console.error('Error fetching job categories:', error);
        // Fallback to empty categories array
        setJobCategories([]);
      } finally {
        setIsCategoriesLoading(false);
      }
    };
    
    if (user && userInfo) {
      fetchCategories();
    }
  }, [user, userInfo]);

  // Fetch premium jobs separately
  React.useEffect(() => {
    const getPremiumJobs = async () => {
      try {
        // Check if userInfo exists before making the API call
        if (!userInfo) {
          console.error('User info not available for fetching premium jobs');
          return;
        }
        const premiumJobs = await fetchFeaturedJobs(userInfo);
        setPremiumJobs(premiumJobs);
      } catch (error) {
        console.error('Error fetching premium jobs:', error);
        setPremiumJobs([]);
      }
    };

    if (user && userInfo) {
      getPremiumJobs();
    }
  }, [user, userInfo]);

  // Fetch regular jobs
  React.useEffect(() => {
    const getJobs = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Check if userInfo exists before making the API call
        if (!userInfo) {
          setError('Authentification requise. Veuillez vous connecter à nouveau.');
          setIsLoading(false);
          return;
        }
        
        let apiUrl = `/jobs?limit=${JOBS_PER_PAGE}&offset=${offset}`;
        
        // Add category filter if selected
        if (selectedCategory) {
          apiUrl += `&category=${selectedCategory}`;
        }
        
        const data = await fetchJobsOffers(apiUrl, userInfo);
        
        // Get jobs data from response
        if (Array.isArray(data)) {
          // Direct array response
          setRegularJobs(data);
          // If we got exactly JOBS_PER_PAGE items, assume there are more
         
         // If we have a selected category, use the total from categoryTotals
         if (selectedCategory && categoryTotals[selectedCategory]) {
           const categoryTotal = categoryTotals[selectedCategory];
           setTotalJobs(categoryTotal);
           setTotalPages(Math.ceil(categoryTotal / JOBS_PER_PAGE));
         } else {
           // Otherwise use the response data to estimate total
           setTotalJobs(Math.max(offset + data.length + (data.length === JOBS_PER_PAGE ? 1 : 0), totalJobs));
           setTotalPages(Math.ceil((offset + data.length + (data.length === JOBS_PER_PAGE ? 1 : 0)) / JOBS_PER_PAGE));
         }
        } else if (data.data && Array.isArray(data.data)) {
          // Paginated response
          setRegularJobs(data.data);
          setTotalJobs(data.total || data.data.length);
          setTotalPages(data.last_page || Math.ceil((data.total || data.data.length) / JOBS_PER_PAGE));
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setError('Impossible de charger les offres d\'emploi pour le moment');
        setRegularJobs([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (user && userInfo) {
      getJobs();
    } else if (user && !userInfo) {
      setError('Authentification requise. Veuillez vous connecter à nouveau.');
      setIsLoading(false);
    }

  }, [currentPage, selectedCategory, user, userInfo, offset, totalJobs, categoryTotals]);

  const handlePageChange = (page: number) => {
    // Update URL with page parameter and preserve category filter
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
    
    // Reset jobs to show loading state
    setRegularJobs([]);
    setIsLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (category: number | null) => {
    // Update URL with category parameter and reset to page 1
    const params = new URLSearchParams();
    if (category) params.set('category', category.toString());
    params.set('page', '1');
    setSearchParams(params);
    
    // Track filter usage in analytics
    if (category) {
      const categoryObj = jobCategories.find((c: any) => c.code === category);
      amplitude.track("Job Filter Applied", {
        category: categoryObj?.name || category
      });
    } else {
      amplitude.track("Job Filter Reset");
    }
    
    // Reset jobs to show loading state
    setRegularJobs([]);
    setIsLoading(true);
  };

  const handlePublishJob = () => {
    // Track the action in analytics
    amplitude.track("Publish Job Button Click");
    // Use openWebRedirect to open the URL
    openWebRedirect('https://pvtistes.net/jobs/index.php?do=edit', userInfo);
  };

  // Handle job click to show modal
  const handleJobClick = async (job: Job) => {
    // Analytics tracking
    amplitude.track("Job Card Click", {
      jobId: job.id,
      jobTitle: job.title || 'Unknown job',
      isPremium: job.featured || false
    });
    
    // If job has full details already, show it directly
    if (job.description) {
      setSelectedJob(job);
      setIsJobModalOpen(true);
      return;
    }
    
    // Otherwise fetch full job details
    setIsJobDetailLoading(true);
    setJobDetailError(null);
    
    try {
      const jobDetail = await fetchJobById(job.id.toString(), userInfo);
      setSelectedJob(jobDetail);
      setIsJobModalOpen(true);
    } catch (error) {
      console.error('Error fetching job details:', error);
      setJobDetailError('Impossible de charger le détail de cette offre');
    } finally {
      setIsJobDetailLoading(false);
    }
  };

  // Close job detail modal
  const handleCloseJobModal = () => {
    setIsJobModalOpen(false);
    setTimeout(() => {
      setSelectedJob(null);
      setJobDetailError(null);
    }, 300); // Delay clearing data until after transition
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col pb-mobile">
      <SEO 
        title="Offres d'emploi PVT/WHV - pvtistes.net"
        description="Trouvez des opportunités d'emploi adaptées aux Pvtistes. Consultez les dernières offres et postulez directement."
        image="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=2574&auto=format&fit=crop"
      />
      
      <Navbar 
        onLogin={() => {}} 
        onAdminClick={() => {}}
        onHomeClick={() => {}}
        isAdminMode={false}
      />

      {/* Hero Section - Hidden on mobile */}
      <div className="hidden sm:flex relative min-h-[400px] sm:min-h-[500px] items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=2574&auto=format&fit=crop"
            alt="Job offers"
            className="w-full h-full object-cover opacity-90 dark:opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/70 to-gray-900/95"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4 sm:mb-6">
              <Briefcase className="w-4 h-4 text-primary-400" />
              <span className="text-xs sm:text-sm font-medium text-white">Emploi</span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
              Offres d'emploi PVT
            </h1>

            <p className="text-base sm:text-lg text-white/90 leading-relaxed mb-6 sm:mb-8">
              Trouvez des opportunités d'emploi adaptées aux Pvtistes. Consultez les dernières offres et postulez directement.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              {/* Job alert signup button */}
              <a 
                href="https://pvtistes.net/jobs/index.php?do=notifications" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors shadow-lg hover:shadow-xl cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  amplitude.track("Job Notifications Click");
                  openWebRedirect("https://pvtistes.net/jobs/index.php?do=notifications", userInfo);
                }}
              >
                <MailCheck className="w-5 h-5" />
                <span className="font-medium">Recevoir les alertes emploi</span>
              </a>
              
              {/* Publish job button */}
              <button
                onClick={handlePublishJob}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white border border-white/20 rounded-xl transition-colors shadow-lg hover:shadow-xl"
              >
                <PlusCircle className="w-5 h-5" />
                <span className="font-medium">Publier un emploi</span>
                <ExternalLink className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-only action buttons */}
      <div className="sm:hidden flex flex-col gap-3 mt-20 mb-4 px-4">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Offres d'emploi PVT
        </h1>
        <div className="flex gap-3">
          <a 
            href="https://pvtistes.net/jobs/index.php?do=notifications" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors shadow-sm cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              amplitude.track("Job Alert Signup Click");
              openWebRedirect("https://pvtistes.net/jobs/index.php?do=notifications", userInfo);
            }}
          >
            <MailCheck className="w-4 h-4" />
            <span className="font-medium text-sm">Alertes emploi</span>
          </a>
          <button
            onClick={handlePublishJob}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700/50 text-gray-900 dark:text-gray-100 rounded-lg transition-colors shadow-sm"
          >
            <PlusCircle className="w-4 h-4" />
            <span className="font-medium text-sm">Publier un emploi</span>
          </button>
        </div>
      </div>

      <main className="flex-1 py-6 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Adding Job Categories Filter */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <JobFilters 
                categories={jobCategories}
                selectedCategory={selectedCategory}
                onFilterChange={handleFilterChange}
                isLoading={isCategoriesLoading}
              />
            </div>
            
            <div className="lg:col-span-3">
              {/* Display premium jobs section */}
              {!isLoading && !error && premiumJobs.length > 0 && (
                <div className="mb-8">
                  <div className="bg-gradient-to-br from-amber-50/50 to-white dark:from-amber-900/10 dark:to-gray-800 rounded-xl p-5 border border-amber-200 dark:border-amber-800/50 shadow-sm mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                        <Award className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                          Offres d'emploi premium
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Opportunités mises en avant par nos employeurs
                        </p>
                      </div>
                    </div>
                  </div>

                  <JobList
                    jobs={premiumJobs}
                    isLoading={false}
                    error={null}
                    isPremium={true}
                    onJobClick={handleJobClick}
                    categoryMap={categoryMap}
                  />
                </div>
              )}
              
              {/* Show active filter */}
              {selectedCategory && (
                <div className="mb-6 bg-primary-50 dark:bg-primary-900/20 rounded-xl p-4 border border-primary-200 dark:border-primary-800/50">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-primary-500" />
                      <span className="text-sm sm:text-base font-medium text-primary-600 dark:text-primary-400">
                        Filtré par : {jobCategories.find((c: any) => c.code === selectedCategory)?.name}
                      </span>
                    </div>
                    <button
                      onClick={() => handleFilterChange(null)}
                      className="text-xs sm:text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                    >
                      Réinitialiser le filtre
                    </button>
                  </div>
                </div>
              )}
              
              {/* Jobs grid */}
              <JobList 
                jobs={regularJobs} 
                isLoading={isLoading} 
                error={error}
                isPremium={false}
                onJobClick={handleJobClick}
                categoryMap={categoryMap}
              />
              
              {/* Pagination component - Explicitly ensure it always appears when there are jobs */}
              {!isLoading && !error && regularJobs.length > 0 && totalPages > 0 && (
                <Pagination 
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  className="mt-8 sm:mt-12 mb-8" 
                />
              )}
              
              {/* Bottom CTA for forum */}
              {!isLoading && !error && regularJobs.length > 0 && (
                <div className="mt-8 sm:mt-16 bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 rounded-xl p-4 sm:p-6 border border-primary-100/50 dark:border-primary-800/30 text-center">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                    Vous cherchez plus d'opportunités ?
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 max-w-2xl mx-auto">
                    Activez les notifications pour recevoir les nouvelles offres d'emploi directement dans votre boîte mail et ne manquez aucune opportunité.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a 
                      href="https://pvtistes.net/jobs/index.php?do=notifications" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 rounded-xl transition-colors border border-primary-100/50 dark:border-primary-800/30 hover:bg-gray-50 dark:hover:bg-gray-700/50 shadow-sm hover:shadow-md cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        amplitude.track("Job Forum Click");
                        openWebRedirect("https://pvtistes.net/jobs/index.php?do=notifications", userInfo);
                      }}
                    >
                      <MailCheck className="w-5 h-5" />
                      <span className="font-medium">Activer les notifications</span>
                    </a>
                    
                    <button
                      onClick={handlePublishJob}
                      className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors shadow-sm hover:shadow-md"
                    >
                      <PlusCircle className="w-5 h-5" />
                      <span className="font-medium">Publier un emploi</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Job Detail Modal */}
      <JobDetailModal
        job={selectedJob}
        isOpen={isJobModalOpen}
        onClose={handleCloseJobModal}
        isLoading={isJobDetailLoading}
        error={jobDetailError}
      />

      <Footer />
    </div>
  );
}